import { useEffect, useRef, useState } from "react";
import { Loader2, Save } from "lucide-react";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { cn } from "../../../lib/utils";
import { FRAUD_ELEMENT_OBSERVATION_CONFIG } from "../logic/fraudElementObservationConfig";
import { selectFraudElementObservation } from "../logic/fraudElementsSlice";
import { upsertFraudElementObservation } from "../logic/fraudElementsThunk";
import { FraudElementContributors } from "../../../api/fraudElements";
import { showErrorToast } from "../../../lib/toast";
import EditToggleButton from "../../../components/ui/button/v2/EditToggleButton";

export interface FraudElementObservationCardProps {
  fraudElementId: string;
  contributor: FraudElementContributors;
  index?: number;
}

export default function FraudElementObservationCard({
  fraudElementId,
  contributor,
  index,
}: FraudElementObservationCardProps) {
  const dispatch = useAppDispatch();

  const observation = useAppSelector((state) =>
    selectFraudElementObservation(state, fraudElementId, contributor)
  );

  const content = observation?.observation ?? "";
  const { title, placeholder } = FRAUD_ELEMENT_OBSERVATION_CONFIG[contributor];

  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(content);
  const [isSaving, setIsSaving] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setDraft(content);
  }, [content]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = "0px";
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [draft, isEditing]);

  useEffect(() => {
    setIsEditing(false);
  }, [fraudElementId]);

  const hasChanges = draft !== content;

  const handleCancel = () => {
    setDraft(content);
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!hasChanges || isSaving) return;

    setIsSaving(true);
    try {
      await dispatch(
        upsertFraudElementObservation({
          fraud_element_id: fraudElementId,
          request: {
            contributor,
            observation: draft,
          },
        })
      );
      setIsEditing(false);
    } catch {
      showErrorToast("Failed to update observations. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className={cn(
        "overflow-hidden transition-all duration-200 bg-white",
        isEditing
          ? "rounded-[14px] border-[3px] border-[#C0C9FB]"
          : "rounded-[14px] border border-[#E5E5E5]"
      )}
    >
      <div className="flex items-center justify-between border-b border-[#E5E5E5] px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="flex h-5 min-w-5 items-center justify-center rounded-[4px] bg-[#EEF2FF] px-1 py-0.5 text-xs font-medium text-[#3551F3] leading-4">
            {String((index ?? 0) + 1).padStart(2, "0")}
          </div>

          <h3 className="text-[16px] font-medium leading-[22px] text-[#0A0A0A]">
            {title}
          </h3>
        </div>

        <EditToggleButton
          isEditing={isEditing}
          onEdit={() => setIsEditing(true)}
          onCancel={handleCancel}
        />
      </div>

      <div className="p-4">
        {isEditing ? (
          <textarea
            ref={textareaRef}
            value={draft}
            disabled={isSaving}
            placeholder={placeholder}
            rows={4}
            onChange={(e) => setDraft(e.target.value)}
            className={cn(
              "w-full resize-none overflow-hidden border-none bg-transparent p-0",
              "text-sm leading-5 text-[#171717]",
              "placeholder:text-[#737373]",
              "focus:outline-none focus:ring-0",
              "min-h-[84px]",
              isSaving && "opacity-60"
            )}
          />
        ) : (
          <div
            className={cn(
              "whitespace-pre-wrap break-words text-sm leading-5 min-h-[84px]",
              content ? "text-[#171717]" : "text-[#737373]"
            )}
          >
            {content || placeholder}
          </div>
        )}
      </div>

      {isEditing && hasChanges && (
        <div className="flex h-[52px] items-center justify-between border-t border-[#E5E5E5] bg-[#F8F9FA] px-4 py-3">
          <span className="text-sm font-medium leading-5 text-[#737373]">
            You have made changes, save now
          </span>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCancel}
              disabled={isSaving}
              className="flex items-center justify-center rounded-[8px] bg-transparent px-4 py-2 text-sm font-medium leading-5 text-[#0A0A0A] opacity-50"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center justify-center gap-2 rounded-[8px] bg-gradient-to-b from-[#788AF7] to-[#3551F3] px-4 py-1 text-sm font-medium leading-5 text-[#FEF2F2] shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition-all hover:opacity-90"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin text-[#FEF2F2]" />
              ) : (
                <Save className="h-4 w-4 text-[#FEF2F2]" />
              )}
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}